import React, {useEffect, useRef, useState, useMemo, useCallback} from 'react';
import Main from "../../modules/main/main";
import usePermissions from "../../services/auth/usePermissions";
import {useNavigate} from "react-router-dom";
import { AgGridReact } from 'ag-grid-react';
import ReactLoading from 'react-loading';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './forms.scss';
import {apiForms} from "../../services/api/forms";

function Forms(props) {
    const navigate = useNavigate();
    const [userCan] = usePermissions();
    const breadcrumbs = [
        {"link": "/", "name": "Forms", "active": true}
    ];

    const gridRef = useRef();
    const hasLoadedRef = useRef(false);
    const [error, setError] = useState('');
    const [paginationPageSize, setPaginationPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [rowData, setRowData] = useState([]);
    const handleOpen = () => {
        navigate('/forms/create');
    };

    const [columnDefs] = useState([
        {
            field: 'name',
            headerName: 'Name'
        },
        {
            field: 'slug',
            headerName: 'Slug'
        },
        {
            field: 'is_active',
            headerName: 'Active',
            valueGetter: params => params.data?.is_active ? 'Yes' : 'No',
            sortable: true,
            filter: true,
        },
        {
            headerName: 'Created By',
            valueGetter: params => {
                if (!params.data?.creator) return '';
                return params.data.creator.name || '';
            },
            sortable: false,
            filter: false,
        },
        {
            headerName: 'Action',
            cellRenderer: params => {
                if(userCan('edit.forms') || userCan('view.forms')) {
                    return (
                        <span>
                            <button className={'btn btn-primary mb-2 mr-2'} onClick={() => navigate('/forms/'+params.data.id)}>Edit</button>
                            <button className={'btn btn-danger mb-2'} onClick={() => handleDelete(params.data.id)}>Delete</button>
                        </span>
                    );
                }
            },
            editable: false,
            sortable: false,
            filter: false,
            minWidth: 200,
        }
    ]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true,
        suppressMenu: true,
        cellStyle: { display: 'flex', alignItems: 'center' }
    }), []);

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this form?')) {
            try {
                await apiForms.delete(id);
                // Refresh the data
                loadForms();
            } catch (err) {
                setError('Failed to delete form');
            }
        }
    };

    const loadForms = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            
            const res = await apiForms.getAll();
            
            let forms = [];
            
            if (res && res.forms) {
                if (res.forms.data && Array.isArray(res.forms.data)) {
                    forms = res.forms.data;
                } else if (Array.isArray(res.forms)) {
                    forms = res.forms;
                }
            } else if (Array.isArray(res)) {
                forms = res;
            }
            
            if (!Array.isArray(forms)) {
                forms = [];
            }
            
            setRowData(forms);
        } catch (err) {
            console.error('Error loading forms:', err);
            let errorMessage = 'Failed to load forms';
            if (err && err.response && err.response.data) {
                errorMessage = err.response.data.message || errorMessage;
            } else if (err && err.message) {
                errorMessage = err.message;
            }
            setError(errorMessage);
            setRowData([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    }, []);

    // Check permissions and redirect if needed
    useEffect(() => {
        if(!userCan('view.forms')){
            navigate('/');
        }
    }, [userCan, navigate]);

    // Load forms data only once on mount
    useEffect(() => {
        if(!userCan('view.forms')){
            return;
        }
        
        // Only load forms once
        if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
            loadForms();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Main title="Forms" breadcrumbs={breadcrumbs} loading={false}>
            {error && <div className="alert alert-danger">
                <h5><i className="icon fas fa-ban"></i> Error!</h5>
                {error}
            </div>}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <label htmlFor="pageSizeSelect" className="mr-2">Page Size:</label>
                    <select
                        id="pageSizeSelect"
                        className="form-control form-control-sm d-inline-block"
                        style={{ width: 'auto' }}
                        value={paginationPageSize}
                        onChange={(e) => {
                            const newPageSize = Number(e.target.value);
                            setPaginationPageSize(newPageSize);
                            if (gridRef.current && gridRef.current.api) {
                                gridRef.current.api.paginationSetPageSize(newPageSize);
                            }
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
                {userCan('create.forms') &&
                    <button className={'btn btn-primary'} onClick={handleOpen}>Create Form</button>
                }
            </div>
            <div className="ag-theme-alpine" style={{ height: 800, width: '100%', position: 'relative' }}>
                <div className={loading ? "overlay" : "overlay d-none"}>
                    <ReactLoading type="spin" color="orange" width={'10%'} height={'10%'} />
                </div>
                <AgGridReact
                    style={{ width: '100%', height: '100%' }}
                    ref={gridRef}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowData={rowData}
                    pagination={true}
                    paginationPageSize={paginationPageSize}
                    getRowId={(params) => params.data.id}
                    onGridReady={(params) => {
                        if (params.api) {
                            params.api.sizeColumnsToFit();
                        }
                    }}
                    onColumnResized={(event) => event.api.sizeColumnsToFit()}
                    getRowHeight={() => 50}
                ></AgGridReact>
            </div>
        </Main>
    );
}

export default Forms;

